import {Button, Modal} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {MIME_TYPES} from '@mantine/dropzone';
import {IconCloudDownload, IconInfoCircle} from '@tabler/icons-react';
import React, {useState} from 'react';
import type {IFormDocumentUploadFile} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import {FormFileUploadV2} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import {useAlert} from 'src/shared/hooks/useAlert';
import ApiCaller, {ApiCallerDownloadFile} from 'src/shared/utils/ApiCaller';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {isValidArray} from 'src/utilities/objectUtils';
import type {IFormFieldV2} from 'src/types/FormV2';

const fieldData: IFormFieldV2 = {
  name: 'equipmentTypeImport',
  label: 'Equipment Type File Upload',
  type: 'file',
  description:
    'Use the file upload below to select a .CSV file containing your equipment type data. The data file must match the import template specification. You may download a copy of the template to match with your data using the "Download Template" button below.',
  fileUploadOptions: {
    allowedFileTypes: [MIME_TYPES.csv],
    isHideUploadSuccessEnabled: true,
  },
};

export interface ImportEquipmentTypesModalProps {
  opened: boolean;
  onClose: () => void;
}

export const ImportEquipmentTypesModal: React.FC<
  ImportEquipmentTypesModalProps
> = ({opened, onClose}) => {
  const {setAlert} = useAlert();
  const [isSaving, setIsSaving] = useState(false);
  const [importedFiles, setImportedFiles] = useState<IFormDocumentUploadFile[]>(
    []
  );

  const handleUpload = (
    _name: string,
    _value: string,
    uploadedFiles: IFormDocumentUploadFile[]
  ) => {
    setImportedFiles(uploadedFiles);
  };

  const handleConfirmImport = async () => {
    if (!isValidArray(importedFiles)) return;

    setIsSaving(true);

    try {
      await ApiCaller({
        method: 'POST',
        endpointURL: ApiEndpoints.equipmentTypeImport,
        data: {
          key: importedFiles[0].encodedKey,
        },
      });
      setAlert(
        'Import started. Results will be emailed to you when processing finishes.',
        'success'
      );
      handleClose();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsSaving(false);
  };

  const handleDownloadImportTemplate = () => {
    ApiCallerDownloadFile(
      ApiEndpoints.equipmentTypeImportTemplate,
      'equipment_type_import_template.csv'
    ).catch((error: unknown) => setAlert(parseErrorMessage(error), 'error'));
  };

  const handleClose = () => {
    setImportedFiles([]);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Import Equipment Types"
      size="lg">
      <Stack gap="md">
        <Alert icon={<IconInfoCircle />} color="blue" title="Before you import">
          Equipment types are matched by name: existing types are updated in
          place and new ones are added with no custom fields. Equipment types
          already on file that are missing from your import are never removed.
          Files may contain up to 10,000 rows, and results are emailed to you
          once processing finishes.
        </Alert>
        <Divider />
        <FormFileUploadV2
          field={fieldData}
          value={importedFiles}
          onChange={handleUpload}
        />
        <Group justify="space-between">
          <Button
            color="dark"
            variant="subtle"
            onClick={handleDownloadImportTemplate}
            leftSection={<IconCloudDownload size={16} />}>
            Download Template
          </Button>
          <Group>
            <Button color="dark" variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmImport}
              disabled={!isValidArray(importedFiles)}
              loading={isSaving}>
              Import
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};
